import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { productionTasksAPI } from '../../api/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { TASKS_CHANGED_EVENT, taskItemLabel } from '../../pages/ProductionTasks';

// No push channel to the browser, so poll: often enough that a note shows
// up within half a minute, and right away when the tab regains focus.
const POLL_MS = 30000;

const SOUND_KEY = 'notificationSound';

const readSoundPref = () => {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off';
  } catch (err) {
    return true;
  }
};

// Browsers only allow audio after the user has interacted with the page,
// so the AudioContext is created/resumed on the first click or key press
// and reused afterwards (it then also plays while the tab is in background).
let audioCtx = null;
const unlockAudio = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === 'suspended') audioCtx.resume();
  } catch (err) {
    // no audio support
  }
};

// Short two-note chime, generated so no sound file is needed.
const playChime = () => {
  if (!audioCtx || audioCtx.state !== 'running') return;
  const now = audioCtx.currentTime;
  [
    [880, 0],
    [1318.5, 0.15],
  ].forEach(([freq, offset]) => {
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, now + offset);
    gain.gain.exponentialRampToValueAtTime(0.3, now + offset + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.4);
    osc.connect(gain).connect(audioCtx.destination);
    osc.start(now + offset);
    osc.stop(now + offset + 0.45);
  });
};

const NotificationBell = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [latest, setLatest] = useState([]);
  const [soundOn, setSoundOn] = useState(readSoundPref);
  const ref = useRef(null);
  // null until the first load, so opening the app with old unread notes
  // doesn't chime -- only notes arriving while it's open do.
  const prevUnread = useRef(null);
  const soundOnRef = useRef(soundOn);
  soundOnRef.current = soundOn;

  const load = useCallback(async () => {
    try {
      const { data } = await productionTasksAPI.getNotifications();
      const count = data.data.unread;
      if (prevUnread.current !== null && count > prevUnread.current && soundOnRef.current) {
        playChime();
      }
      prevUnread.current = count;
      setUnread(count);
      setLatest(data.data.latest);
    } catch (err) {
      // Silent: the bell just keeps its last known state.
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, location.pathname]);

  useEffect(() => {
    window.addEventListener('pointerdown', unlockAudio);
    window.addEventListener('keydown', unlockAudio);
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') load();
    };
    // Keeps polling in a background tab too, so the chime still plays there.
    const timer = setInterval(load, POLL_MS);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener(TASKS_CHANGED_EVENT, load);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener(TASKS_CHANGED_EVENT, load);
    };
  }, [load]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openTask = async (task) => {
    setOpen(false);
    if (task.assigned_to === user?.id && !task.read_at) {
      try {
        await productionTasksAPI.markRead(task.id);
      } catch (err) {
        // ignore
      }
    }
    navigate(`/production-tasks?open=${task.id}`);
    load();
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    try {
      localStorage.setItem(SOUND_KEY, next ? 'on' : 'off');
    } catch (err) {
      // preference just won't persist
    }
    if (next) {
      unlockAudio();
      playChime();
    }
  };

  const markAllRead = async () => {
    try {
      await productionTasksAPI.markAllRead();
      load();
    } catch (err) {
      // ignore
    }
  };

  return (
    <div className='relative' ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className='relative p-1.5 rounded-md hover:bg-gray-300 transition'
        title={t('productionTasks.notifications')}
      >
        <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
          />
        </svg>
        {unread > 0 && (
          <span className='absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center'>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className='absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-2xl z-50 border border-gray-200'>
          <div className='flex items-center justify-between px-4 py-3 border-b border-gray-100'>
            <p className='text-sm font-semibold text-gray-800'>{t('productionTasks.notifications')}</p>
            <div className='flex items-center gap-3'>
              {unread > 0 && (
                <button onClick={markAllRead} className='text-xs text-blue-600 hover:underline'>
                  {t('productionTasks.markAllRead')}
                </button>
              )}
              <button
                onClick={toggleSound}
                className='text-base leading-none'
                title={soundOn ? t('productionTasks.soundOn') : t('productionTasks.soundOff')}
              >
                {soundOn ? '🔊' : '🔇'}
              </button>
            </div>
          </div>
          <div className='max-h-96 overflow-y-auto'>
            {latest.length === 0 ? (
              <p className='px-4 py-6 text-sm text-gray-500 text-center'>{t('productionTasks.noNotifications')}</p>
            ) : (
              latest.map((task) => {
                const isNewNote = task.assigned_to === user?.id && !task.read_at;
                const hasNewComments = task.unread_comments > 0;
                const highlighted = isNewNote || hasNewComments;
                return (
                <button
                  key={task.id}
                  onClick={() => openTask(task)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 ${
                    highlighted ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className='flex items-start gap-2'>
                    {highlighted && <span className='mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0' />}
                    <div className='min-w-0'>
                      <p className='text-sm text-gray-800 truncate'>
                        <span className='font-medium'>{t('productionTasks.sendToProduce')}:</span>{' '}
                        {taskItemLabel(task)}
                        {task.quantity ? ` × ${task.quantity}` : ''}
                      </p>
                      {hasNewComments ? (
                        <p className='text-xs text-gray-700 truncate'>
                          💬 <span className='font-medium'>{task.last_comment_by}:</span> {task.last_comment_body}
                          {task.unread_comments > 1 ? ` (+${task.unread_comments - 1})` : ''}
                        </p>
                      ) : (
                        task.note &&
                        task.item_type !== 'text' && <p className='text-xs text-gray-500 truncate'>{task.note}</p>
                      )}
                      <p className='text-xs text-gray-400 mt-0.5'>
                        {hasNewComments
                          ? new Date(task.last_comment_at).toLocaleString()
                          : `${task.created_by_name} · ${new Date(task.created_at).toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                </button>
                );
              })
            )}
          </div>
          <button
            onClick={() => {
              setOpen(false);
              navigate('/production-tasks');
            }}
            className='w-full px-4 py-2 text-sm text-center text-blue-600 hover:bg-gray-50 rounded-b-lg'
          >
            {t('productionTasks.viewAll')}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
